// VOY-485 (rebuild) — Travel Companions (Settings tab) — DECLARED INPUT.
//
// Builds VOY-460's UI (orphaned by the previous ticket rescope) against
// its documented endpoint contracts (see VOY-460 Linear comments):
//
//   GET    /api/v2/profile/:userId/companions
//   POST   /api/v2/profile/:userId/companions
//   PATCH  /api/v2/profile/:userId/companions/:id
//   DELETE /api/v2/profile/:userId/companions/:id   (soft-delete)
//
// All four wired through the shipped lib helpers; no new endpoints.
//
// PRIVACY RULE (hard requirement from the ticket + VOY-460's contract):
//   - **Never display raw companion date_of_birth.** We compute `age_band`
//     client-side from DOB and render only the band ("Child", "Adult",
//     etc.). The user enters DOB themselves when creating/editing —
//     they own the data — but the read-mode never echoes the value back.
//   - Edit form does NOT pre-fill DOB. To change it the user re-enters
//     it. This is intentional: the value is sensitive, and the API
//     never returns it back to a list view either (the `/agent-context`
//     projection strips it; only the owner-readable companions endpoint
//     carries it).
//   - Never log full DOB anywhere.

import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import {
  getTravelCompanions,
  createTravelCompanion,
  updateTravelCompanion,
  deleteTravelCompanion,
} from '@/lib/profile'
import type { TravelCompanion, Relationship } from '@/types/profile'

export interface TravelCompanionsSectionProps {
  userId: string
}

const RELATIONSHIP_OPTIONS: { value: Relationship; label: string }[] = [
  { value: 'partner', label: 'Partner' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'friend', label: 'Friend' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'family_other', label: 'Family (other)' },
  { value: 'other', label: 'Other' },
]

interface CompanionDraft {
  display_name: string
  relationship: Relationship | ''
  date_of_birth: string  // Owner-entered. Cleared on edit-open — see header.
  dietary_csv: string
  mobility_notes: string
  passport_country: string
}

const EMPTY_DRAFT: CompanionDraft = {
  display_name: '',
  relationship: '',
  date_of_birth: '',
  dietary_csv: '',
  mobility_notes: '',
  passport_country: '',
}

/**
 * Client-side derivation of age_band from DOB string. Mirrors the
 * backend rule (see VOY-460): infant <2, child 2–11, teen 12–17,
 * adult 18–64, senior 65+. Returns null if DOB unparseable.
 */
function ageBand(dob: string | null | undefined): 'infant' | 'child' | 'teen' | 'adult' | 'senior' | null {
  if (!dob) return null
  const d = new Date(dob)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  const years = (now.getTime() - d.getTime()) / (365.25 * 24 * 3600 * 1000)
  if (years < 2) return 'infant'
  if (years < 12) return 'child'
  if (years < 18) return 'teen'
  if (years < 65) return 'adult'
  return 'senior'
}

function ageBandLabel(b: ReturnType<typeof ageBand>): string {
  if (!b) return ''
  return b.charAt(0).toUpperCase() + b.slice(1)
}

export function TravelCompanionsSection({ userId }: TravelCompanionsSectionProps) {
  const [companions, setCompanions] = useState<TravelCompanion[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [draft, setDraft] = useState<CompanionDraft>(EMPTY_DRAFT)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  async function reload() {
    setLoading(true)
    try {
      setCompanions(await getTravelCompanions(userId))
    } catch (err) {
      console.error('[TravelCompanionsSection] load failed:', err)
    } finally {
      setLoading(false)
    }
  }

  function beginCreate() {
    setIsCreating(true)
    setEditingId(null)
    setDraft(EMPTY_DRAFT)
    setError(null)
  }

  function beginEdit(c: TravelCompanion) {
    // PRIVACY RULE: do not pre-fill date_of_birth even on edit. The user
    // can leave it blank to keep the stored value, or re-enter it to
    // change it. See header comment.
    setIsCreating(false)
    setEditingId(c.id)
    setDraft({
      display_name: c.display_name,
      relationship: c.relationship ?? '',
      date_of_birth: '',
      dietary_csv: (c.dietary_restrictions ?? []).join(', '),
      mobility_notes: c.mobility_notes ?? '',
      passport_country: c.passport_country ?? '',
    })
    setError(null)
  }

  function cancel() {
    setIsCreating(false)
    setEditingId(null)
    setDraft(EMPTY_DRAFT)
    setError(null)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!draft.display_name.trim()) {
      setError('Display name is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const dietary = draft.dietary_csv
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
      const base = {
        display_name: draft.display_name.trim(),
        relationship: draft.relationship || null,
        dietary_restrictions: dietary,
        mobility_notes: draft.mobility_notes.trim() || null,
        passport_country: draft.passport_country.trim() || null,
      }
      if (editingId) {
        // On edit, only include date_of_birth in the patch if the user
        // entered a fresh value — otherwise leave the stored DOB alone.
        const patch: Partial<typeof base> & { date_of_birth?: string } = base
        if (draft.date_of_birth) patch.date_of_birth = draft.date_of_birth
        await updateTravelCompanion(userId, editingId, patch)
      } else {
        await createTravelCompanion(userId, {
          ...base,
          date_of_birth: draft.date_of_birth || null,
        })
      }
      cancel()
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(id: string) {
    // Confirm before deleting. Companions can have linked notes
    // (companion_specific scope, FK CASCADE per VOY-460 Migration 2),
    // so removing a companion silently also wipes any companion-specific
    // notes about them. Soft-delete only — the row stays in the table
    // with active=false — but the user shouldn't lose this state to a
    // misclick.
    const c = companions.find((x) => x.id === id)
    if (!window.confirm(`Remove ${c?.display_name ?? 'this companion'}?\n\nAny notes you wrote about them will be removed too.`)) return
    setSaving(true)
    try {
      await deleteTravelCompanion(userId, id)
      if (editingId === id) cancel()
      await reload()
    } catch (err) {
      console.error('[TravelCompanionsSection] delete failed:', err)
    } finally {
      setSaving(false)
    }
  }

  const editing = editingId !== null || isCreating

  return (
    <section className="card" aria-labelledby="companions-h">
      <div className="card-h">
        <h3 id="companions-h">Travel companions</h3>
        <div className="row" style={{ gap: 8 }}>
          <span className="meta">{loading ? 'LOADING…' : `${companions.length} SAVED`}</span>
          {!editing && (
            <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={beginCreate}>
              <Plus aria-hidden="true" style={{ width: 12, height: 12 }} />
              Add
            </button>
          )}
        </div>
      </div>

      {!loading && companions.length === 0 && !editing && (
        <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>
          Add the people you travel with so Voy can plan for everyone — dietary needs, ages, mobility.
        </p>
      )}

      {editing && (
        <form onSubmit={submit} className="stack" style={{ gap: 12, marginBottom: 14 }}>
          <div className="row" style={{ gap: 12 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-l">DISPLAY NAME</span>
              <input
                autoFocus
                value={draft.display_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
                maxLength={100}
                placeholder="e.g. Alex"
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-l">RELATIONSHIP</span>
              <select
                value={draft.relationship}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setDraft((d) => ({ ...d, relationship: e.target.value as Relationship | '' }))}
              >
                <option value="">—</option>
                {RELATIONSHIP_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <div className="row" style={{ gap: 12 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-l">{editingId ? 'NEW DATE OF BIRTH (LEAVE BLANK TO KEEP)' : 'DATE OF BIRTH'}</span>
              <input
                type="date"
                value={draft.date_of_birth}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, date_of_birth: e.target.value }))}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-l">PASSPORT COUNTRY</span>
              <input
                value={draft.passport_country}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, passport_country: e.target.value.toUpperCase() }))}
                maxLength={3}
                placeholder="e.g. USA"
              />
            </label>
          </div>
          <label className="field">
            <span className="field-l">DIETARY RESTRICTIONS (COMMA-SEPARATED)</span>
            <input
              value={draft.dietary_csv}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, dietary_csv: e.target.value }))}
              placeholder="e.g. nut_allergy, vegetarian"
            />
          </label>
          <label className="field">
            <span className="field-l">MOBILITY NOTES</span>
            <input
              value={draft.mobility_notes}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, mobility_notes: e.target.value }))}
              maxLength={500}
              placeholder="e.g. uses wheelchair on long walks"
            />
          </label>
          {error && <p style={{ color: 'var(--v6-neg)', fontSize: 12, margin: 0 }} role="alert">{error}</p>}
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" style={{ padding: '6px 12px', fontSize: 11.5 }} onClick={cancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" style={{ padding: '6px 12px', fontSize: 11.5 }} disabled={saving || !draft.display_name.trim()}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add companion'}
            </button>
          </div>
        </form>
      )}

      {companions.length > 0 && (
        <ul className="companion-list">
          {companions.map((c) => {
            const band = ageBand(c.date_of_birth)
            return (
              <li key={c.id} className="companion-row">
                <div className="companion-row-body">
                  <div className="companion-row-name">{c.display_name}</div>
                  <div className="companion-row-meta">
                    {[
                      c.relationship && (RELATIONSHIP_OPTIONS.find((r) => r.value === c.relationship)?.label ?? c.relationship),
                      ageBandLabel(band) || null,
                      c.dietary_restrictions?.length ? `${c.dietary_restrictions.length} dietary` : null,
                      c.mobility_notes ? 'mobility notes' : null,
                      c.passport_country ? `passport: ${c.passport_country}` : null,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <button type="button" className="note-action" onClick={() => beginEdit(c)} aria-label={`Edit ${c.display_name}`}><Pencil aria-hidden="true" /></button>
                  <button type="button" className="note-action danger" onClick={() => void remove(c.id)} aria-label={`Remove ${c.display_name}`}><Trash2 aria-hidden="true" /></button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
